/**
 * TrainingHistoryTab - Sent training history with PDF export
 * Extracted from TrainingView.jsx
 */

import { useState } from 'react';
import { toast } from 'sonner';
import axios from 'axios';
import { User, Truck, RefreshCw, Download, Calendar } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function TrainingHistoryTab({ links, refreshHistory, lastRefresh }) {
  const [showExportModal, setShowExportModal] = useState(false);
  const [exportStartDate, setExportStartDate] = useState('');
  const [exportEndDate, setExportEndDate] = useState('');
  const [exporting, setExporting] = useState(false);

  const getStatusColor = (status) => {
    switch(status) {
      case 'watched': return 'bg-green-500/20 text-green-400';
      case 'opened': return 'bg-blue-500/20 text-blue-400';
      case 'sent': return 'bg-yellow-500/20 text-yellow-400';
      default: return 'bg-gray-500/20 text-gray-400';
    }
  };

  const exportHistoryPDF = async () => {
    setExporting(true);
    try {
      let url = `${API}/training/history/export`;
      const params = new URLSearchParams();
      if (exportStartDate) params.append('start_date', exportStartDate);
      if (exportEndDate) params.append('end_date', exportEndDate);
      if (params.toString()) url += `?${params.toString()}`;
      
      const response = await axios.get(url, {
        withCredentials: true,
        responseType: 'blob'
      });
      
      const blob = new Blob([response.data], { type: 'application/pdf' });
      const downloadUrl = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = downloadUrl;
      link.download = `training_history_${new Date().toISOString().slice(0,10)}.pdf`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(downloadUrl);
      
      toast.success('PDF downloaded successfully!');
      setShowExportModal(false);
    } catch (e) {
      toast.error('Failed to export PDF');
      console.error(e);
    } finally {
      setExporting(false);
    }
  };

  return (
    <>
      <div className="bg-card/60 backdrop-blur-xl rounded-2xl border border-border/30 overflow-hidden">
        <div className="p-4 border-b border-border/30 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <h3 className="text-lg font-bold">Training Links History</h3>
            <span className="text-xs text-muted-foreground bg-secondary px-2 py-1 rounded-lg">
              Auto-refresh: {lastRefresh.toLocaleTimeString()}
            </span>
            <button
              onClick={refreshHistory}
              className="p-2 hover:bg-accent/20 rounded-lg text-accent transition-colors"
              title="Refresh now"
              data-testid="refresh-history-btn"
            >
              <RefreshCw className="w-4 h-4" />
            </button>
          </div>
          <button
            onClick={() => setShowExportModal(true)}
            className="px-4 py-2 bg-green-500 hover:bg-green-600 text-white rounded-xl font-medium flex items-center gap-2 transition-colors"
            data-testid="export-pdf-btn"
          >
            <Download className="w-4 h-4" />
            Export PDF
          </button>
        </div>
        <div className="divide-y divide-border/30">
          {(!Array.isArray(links) || links.length === 0) ? (
            <div className="p-8 text-center text-muted-foreground">
              No training links sent yet. Use the "Send Training" tab to send training to drivers.
            </div>
          ) : (
            links.map((link) => (
              <div key={link.link_token} className="p-4 hover:bg-accent/5">
                <div className="flex items-center gap-4">
                  <div className="flex-1">
                    <p className="font-medium">{link.video_title || 'Training Video'}</p>
                    <div className="flex items-center gap-2 mt-1">
                      <span className="text-sm text-muted-foreground flex items-center gap-1">
                        <User className="w-3 h-3" /> {link.driver_name || link.driver_id}
                      </span>
                      {link.truck_number && (
                        <span className="text-sm text-orange-400 flex items-center gap-1">
                          <Truck className="w-3 h-3" /> {link.truck_number}
                        </span>
                      )}
                    </div>
                    <p className="text-xs text-muted-foreground mt-1">
                      Sent: {link.sent_at ? new Date(link.sent_at).toLocaleString() : new Date(link.created_at).toLocaleString()}
                      {link.opened_at && (
                        <span className="ml-2">• Opened: {new Date(link.opened_at).toLocaleString()}</span>
                      )}
                    </p>
                  </div>
                  <div className="text-right">
                    <span className={`text-xs px-3 py-1 rounded-lg ${getStatusColor(link.status)}`}>
                      {link.status?.toUpperCase() || 'PENDING'}
                    </span>
                    <p className="text-sm font-bold mt-1">
                      Progress: {link.progress || 0}%
                    </p>
                  </div>
                </div>
              </div>
            ))
          )}
        </div>
      </div>

      {/* Export PDF Modal */}
      {showExportModal && (
        <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-50" onClick={() => setShowExportModal(false)}>
          <div className="bg-card rounded-2xl p-6 max-w-md w-full mx-4" onClick={e => e.stopPropagation()}>
            <h3 className="text-xl font-bold mb-2 flex items-center gap-2">
              <Download className="w-6 h-6 text-green-500" />
              Export Training History
            </h3>
            <p className="text-sm text-muted-foreground mb-6">
              Download a PDF report of all training history. Optionally filter by date range.
            </p>
            
            <div className="space-y-4">
              <div>
                <label className="text-sm text-muted-foreground flex items-center gap-2">
                  <Calendar className="w-4 h-4" />
                  Start Date (optional)
                </label>
                <input
                  type="date"
                  value={exportStartDate}
                  onChange={e => setExportStartDate(e.target.value)}
                  className="w-full mt-1 px-4 py-3 rounded-xl bg-secondary border border-border focus:border-accent outline-none"
                  data-testid="export-start-date"
                />
              </div>
              <div>
                <label className="text-sm text-muted-foreground flex items-center gap-2">
                  <Calendar className="w-4 h-4" />
                  End Date (optional)
                </label>
                <input
                  type="date"
                  value={exportEndDate}
                  onChange={e => setExportEndDate(e.target.value)}
                  className="w-full mt-1 px-4 py-3 rounded-xl bg-secondary border border-border focus:border-accent outline-none"
                  data-testid="export-end-date"
                />
              </div>
              
              <div className="p-3 bg-blue-500/10 rounded-xl border border-blue-500/30">
                <p className="text-sm text-blue-400">
                  💡 Leave dates empty to export <strong>ALL history</strong>
                </p>
              </div>
            </div>
            
            <div className="flex gap-3 mt-6">
              <button 
                onClick={() => setShowExportModal(false)} 
                className="flex-1 py-3 bg-secondary rounded-xl font-medium"
              >
                Cancel
              </button>
              <button 
                onClick={exportHistoryPDF}
                disabled={exporting}
                className="flex-1 py-3 bg-green-500 hover:bg-green-600 text-white rounded-xl font-bold flex items-center justify-center gap-2 disabled:opacity-50"
                data-testid="download-pdf-btn"
              >
                {exporting ? (
                  <>
                    <RefreshCw className="w-5 h-5 animate-spin" />
                    Generating...
                  </>
                ) : (
                  <>
                    <Download className="w-5 h-5" />
                    Download PDF
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
